/* Mahi Wairua — metal print viewer (prototype).
   Adds Photos / 3D / Room scenes / Size tabs to the product gallery.
   - Photos is the theme's own gallery, untouched, and stays the fallback.
   - 3D is a real WebGL model: a panel with the selected size and thickness, the artwork on the front,
     metal edges and a plain back. It only loads when the 3D tab is opened. No hardware is drawn.
   - Room scenes and Size are drawn to scale from the selected variant and labelled illustrative. */
(function (root) {
  'use strict';

  /* ---------- Pure helpers (exported for tests) ---------- */
  const H = {
    // "300 × 400mm" -> {w:300, h:400} in mm (first number = width)
    parseSize(label) {
      if (typeof label !== 'string') return null;
      const nums = (label.match(/\d+(?:\.\d+)?/g) || []).map(Number).filter(n => n > 0);
      if (nums.length < 2) return null;
      const lc = label.toLowerCase();
      let unit = 1;
      if (/''|"|”|″|\binch|\bin\b/.test(lc)) unit = 25.4;
      else if (/cm/.test(lc)) unit = 10;
      const w = Math.round(nums[0] * unit * 10) / 10, h = Math.round(nums[1] * unit * 10) / 10;
      if (w > 5000 || h > 5000) return null;
      return { w, h };
    },
    // "Zintech 0.9mm" -> {name:'Zintech', key:'zintech', thickness:0.9}
    parseMaterial(label) {
      const s = String(label || '').trim();
      const m = s.match(/(\d+(?:\.\d+)?)\s*mm/i);
      const name = s.replace(/\s*\d+(?:\.\d+)?\s*mm/i, '').trim() || s;
      return { name, key: name.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, ''), thickness: m ? Number(m[1]) : null };
    },
    // UV window that crops the artwork to the panel's proportions (object-fit: cover)
    coverUV(imgW, imgH, panelW, panelH) {
      const ai = imgW / imgH, ap = panelW / panelH;
      if (Math.abs(ai - ap) < 1e-3) return { u0: 0, u1: 1, v0: 0, v1: 1, cropped: false };
      if (ai > ap) { const k = ap / ai; return { u0: (1 - k) / 2, u1: 1 - (1 - k) / 2, v0: 0, v1: 1, cropped: true }; }
      const k = ai / ap; return { u0: 0, u1: 1, v0: (1 - k) / 2, v1: 1 - (1 - k) / 2, cropped: true };
    },
    // Panel face/edge geometry: returns {front, rest} with interleaved [x,y,z, nx,ny,nz, u,v] floats
    panelGeometry(w, h, t, uv) {
      const x = w / 2, y = h / 2, z = t / 2;
      const quad = (a, b, c, d, n, uvs) => {
        const v = [a, b, c, a, c, d], tex = uvs ? [uvs[0], uvs[1], uvs[2], uvs[0], uvs[2], uvs[3]] : [[0, 0], [0, 0], [0, 0], [0, 0], [0, 0], [0, 0]];
        const out = [];
        v.forEach((p, i) => out.push(p[0], p[1], p[2], n[0], n[1], n[2], tex[i][0], tex[i][1]));
        return out;
      };
      const u = uv || { u0: 0, u1: 1, v0: 0, v1: 1 };
      const front = quad([-x, -y, z], [x, -y, z], [x, y, z], [-x, y, z], [0, 0, 1],
        [[u.u0, u.v0], [u.u1, u.v0], [u.u1, u.v1], [u.u0, u.v1]]);
      const rest = [].concat(
        quad([x, -y, -z], [-x, -y, -z], [-x, y, -z], [x, y, -z], [0, 0, -1]),   // back
        quad([x, -y, z], [x, -y, -z], [x, y, -z], [x, y, z], [1, 0, 0]),        // right edge
        quad([-x, -y, -z], [-x, -y, z], [-x, y, z], [-x, y, -z], [-1, 0, 0]),   // left edge
        quad([-x, y, z], [x, y, z], [x, y, -z], [-x, y, -z], [0, 1, 0]),        // top edge
        quad([-x, -y, -z], [x, -y, -z], [x, -y, z], [-x, -y, z], [0, -1, 0])    // bottom edge
      );
      return { front: new Float32Array(front), rest: new Float32Array(rest) };
    },
    // Photo room scene: place a print of w x h mm on a calibrated wall photo.
    // scene = {imgW, imgH, cx, cy, refPx, refMm}: cx/cy = where the print's centre goes (0-1 of the photo),
    // refPx/refMm = a known width on the wall plane (e.g. a sofa measured in the photo). Returns % box.
    placeOnPhoto(scene, wmm, hmm) {
      const pxPerMm = scene.refPx / scene.refMm;
      const wPx = wmm * pxPerMm, hPx = hmm * pxPerMm;
      return { left: (scene.cx * scene.imgW - wPx / 2) / scene.imgW * 100, top: (scene.cy * scene.imgH - hPx / 2) / scene.imgH * 100, width: wPx / scene.imgW * 100, height: hPx / scene.imgH * 100 };
    },
    // "Lounge|https://…/lounge.jpg|2400|1600|0.5|0.36|900|2100" -> scene object (blank or invalid -> null)
    parseScene(str) {
      const f = String(str || '').split('|').map(x => x.trim());
      if (f.length < 8 || !/^https:\/\//.test(f[1])) return null;
      const n = f.slice(2).map(Number);
      if (n.some(x => !(x > 0))) return null;
      return { name: f[0] || 'Room', src: f[1], imgW: n[0], imgH: n[1], cx: n[2], cy: n[3], refPx: n[4], refMm: n[5] };
    },
    // Choose the flat artwork for a variant: an image whose description starts with the variant's design level
    // (e.g. "Standard … sample artwork"), otherwise the first artwork image.
    pickArtwork(artworks, tier) {
      if (!artworks || !artworks.length) return null;
      const t = String(tier || '').toLowerCase().trim();
      if (t) { const hit = artworks.find(a => String(a.alt || '').toLowerCase().indexOf(t) === 0); if (hit) return hit; }
      return artworks[0];
    },
    fmtCm(mm) { const cm = mm / 10; return (Math.round(cm * 10) / 10).toString().replace(/\.0$/, '') + ' cm'; }
  };

  /* ---------- Tiny matrix library (column-major) ---------- */
  const M = {
    perspective(fovy, aspect, near, far) {
      const f = 1 / Math.tan(fovy / 2), nf = 1 / (near - far);
      return [f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * nf, -1, 0, 0, 2 * far * near * nf, 0];
    },
    mul(a, b) {
      const o = new Array(16);
      for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) {
        let s = 0; for (let k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k]; o[c * 4 + r] = s;
      }
      return o;
    },
    translate(x, y, z) { return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, x, y, z, 1]; },
    rotY(a) { const c = Math.cos(a), s = Math.sin(a); return [c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1]; },
    rotX(a) { const c = Math.cos(a), s = Math.sin(a); return [1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1]; },
    normal3(m) { return [m[0], m[1], m[2], m[4], m[5], m[6], m[8], m[9], m[10]]; } // rotation-only model
  };
  H.M = M;

  if (typeof module !== 'undefined' && module.exports) { module.exports = H; return; }
  root.MWMetal = H;

  /* ---------- WebGL panel renderer ---------- */
  const VS = 'attribute vec3 aPos;attribute vec3 aNor;attribute vec2 aUV;uniform mat4 uModel;uniform mat4 uVP;uniform mat3 uNM;varying vec3 vN;varying vec3 vP;varying vec2 vUV;void main(){vec4 p=uModel*vec4(aPos,1.0);vP=p.xyz;vN=normalize(uNM*aNor);vUV=aUV;gl_Position=uVP*p;}';
  const FS = 'precision mediump float;varying vec3 vN;varying vec3 vP;varying vec2 vUV;uniform sampler2D uTex;uniform bool uUseTex;uniform vec3 uColor;uniform vec3 uEye;uniform float uSpec;uniform float uShine;' +
    'void main(){vec3 n=normalize(vN);vec3 base=uUseTex?texture2D(uTex,vUV).rgb:uColor;vec3 l1=normalize(vec3(0.45,0.6,0.85));vec3 l2=normalize(vec3(-0.6,0.2,0.5));vec3 v=normalize(uEye-vP);' +
    'float d=max(dot(n,l1),0.0)*0.75+max(dot(n,l2),0.0)*0.25;vec3 h=normalize(l1+v);float s=pow(max(dot(n,h),0.0),uShine)*uSpec;vec3 c=base*(0.42+0.68*d)+vec3(s);gl_FragColor=vec4(c,1.0);}';

  class PanelGL {
    constructor(canvas) {
      this.canvas = canvas;
      const gl = canvas.getContext('webgl', { antialias: true, alpha: true, preserveDrawingBuffer: false }) || canvas.getContext('experimental-webgl');
      if (!gl) throw new Error('webgl');
      this.gl = gl;
      const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s)); return s; };
      const p = gl.createProgram();
      gl.attachShader(p, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(p, sh(gl.FRAGMENT_SHADER, FS)); gl.linkProgram(p);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error('link');
      this.p = p; gl.useProgram(p);
      this.loc = {};
      ['aPos', 'aNor', 'aUV'].forEach(n => { this.loc[n] = gl.getAttribLocation(p, n); });
      ['uModel', 'uVP', 'uNM', 'uTex', 'uUseTex', 'uColor', 'uEye', 'uSpec', 'uShine'].forEach(n => { this.loc[n] = gl.getUniformLocation(p, n); });
      this.bufFront = gl.createBuffer(); this.bufRest = gl.createBuffer();
      this.tex = null;
      gl.enable(gl.DEPTH_TEST);
      // Orbit state (current and target)
      this.s = { yaw: -0.35, pitch: 0.12, dist: 1, tx: 0, ty: 0 };
      this.t = { yaw: -0.35, pitch: 0.12, dist: 1, tx: 0, ty: 0 };
      this.zoom = 1;
      this.metal = [0.62, 0.64, 0.66];
      this.dims = { w: 0.3, h: 0.4, t: 0.0009 };
    }
    setTexture(img) {
      const gl = this.gl;
      this.tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, this.tex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, img);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      this.imgW = img.naturalWidth || img.width; this.imgH = img.naturalHeight || img.height;
    }
    // w, h, t in millimetres
    setPanel(wmm, hmm, tmm, metal) {
      const w = wmm / 1000, h = hmm / 1000, t = Math.max(tmm || 0.9, 0.1) / 1000;
      this.dims = { w, h, t };
      if (metal) this.metal = metal;
      const uv = this.imgW ? H.coverUV(this.imgW, this.imgH, wmm, hmm) : null;
      const g = H.panelGeometry(w, h, t, uv);
      const gl = this.gl;
      gl.bindBuffer(gl.ARRAY_BUFFER, this.bufFront); gl.bufferData(gl.ARRAY_BUFFER, g.front, gl.STATIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.bufRest); gl.bufferData(gl.ARRAY_BUFFER, g.rest, gl.STATIC_DRAW);
      this.nFront = g.front.length / 8; this.nRest = g.rest.length / 8;
      this.fitDist = this.fit();
      this.cropped = uv ? uv.cropped : false;
    }
    fit() {
      const a = this.canvas.clientWidth / Math.max(this.canvas.clientHeight, 1) || 1;
      const half = Math.max(this.dims.h / 2, this.dims.w / 2 / a);
      return half / Math.tan(this.fov() / 2) * 1.32;
    }
    fov() { return 30 * Math.PI / 180; }
    view(name) {
      const T = this.t;
      // Keep the turn short: pick the equivalent angle nearest the current one.
      const near = a => a + Math.round((this.t.yaw - a) / (2 * Math.PI)) * 2 * Math.PI;
      if (name === 'front') { T.yaw = near(0); T.pitch = 0; T.tx = 0; T.ty = 0; this.zoom = 1; }
      else if (name === 'back') { T.yaw = near(Math.PI); T.pitch = 0; T.tx = 0; T.ty = 0; this.zoom = 1; }
      else if (name === 'edge') {
        // Close-up of the top-right corner, framed ~45 mm across so the real panel thickness is visible.
        T.yaw = near(-1.15); T.pitch = 0.32; T.tx = this.dims.w / 2; T.ty = this.dims.h / 2;
        this.zoom = Math.max(0.03, (0.045 / 2 / Math.tan(this.fov() / 2)) / this.fitDist);
      }
      else { T.yaw = near(-0.35); T.pitch = 0.12; T.tx = 0; T.ty = 0; this.zoom = 1; }
    }
    step() {
      const s = this.s, T = this.t, k = 0.16;
      T.dist = this.fitDist * this.zoom;
      let moving = false;
      ['yaw', 'pitch', 'dist', 'tx', 'ty'].forEach(key => {
        const d = T[key] - s[key];
        if (Math.abs(d) > 1e-5) { s[key] += d * k; moving = true; } else s[key] = T[key];
      });
      return moving;
    }
    draw() {
      const gl = this.gl, c = this.canvas;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const W = Math.round(c.clientWidth * dpr), Ht = Math.round(c.clientHeight * dpr);
      if (c.width !== W || c.height !== Ht) { c.width = W; c.height = Ht; this.fitDist = this.fit(); }
      gl.viewport(0, 0, c.width, c.height);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      const s = this.s;
      const proj = M.perspective(this.fov(), c.width / Math.max(c.height, 1), Math.max(0.0005, s.dist * 0.05), 20);
      const viewM = M.translate(0, 0, -s.dist);
      // Rotate the panel about its own centre, then move the point of interest (tx along its width) to the screen centre.
      const rot = M.mul(M.rotX(s.pitch), M.rotY(s.yaw));
      const pivot = M.mul(rot, M.translate(-s.tx, -s.ty, 0));
      const model = pivot;
      gl.uniformMatrix4fv(this.loc.uModel, false, new Float32Array(model));
      gl.uniformMatrix4fv(this.loc.uVP, false, new Float32Array(M.mul(proj, viewM)));
      gl.uniformMatrix3fv(this.loc.uNM, false, new Float32Array(M.normal3(rot)));
      gl.uniform3f(this.loc.uEye, 0, 0, s.dist);
      const bind = buf => {
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.enableVertexAttribArray(this.loc.aPos); gl.vertexAttribPointer(this.loc.aPos, 3, gl.FLOAT, false, 32, 0);
        gl.enableVertexAttribArray(this.loc.aNor); gl.vertexAttribPointer(this.loc.aNor, 3, gl.FLOAT, false, 32, 12);
        gl.enableVertexAttribArray(this.loc.aUV); gl.vertexAttribPointer(this.loc.aUV, 2, gl.FLOAT, false, 32, 24);
      };
      // Front: artwork with a gloss varnish highlight
      bind(this.bufFront);
      gl.uniform1i(this.loc.uUseTex, this.tex ? 1 : 0);
      gl.uniform3fv(this.loc.uColor, [0.85, 0.83, 0.8]);
      gl.uniform1f(this.loc.uSpec, 0.22); gl.uniform1f(this.loc.uShine, 60);
      if (this.tex) { gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, this.tex); gl.uniform1i(this.loc.uTex, 0); }
      gl.drawArrays(gl.TRIANGLES, 0, this.nFront);
      // Back and edges: plain metal
      bind(this.bufRest);
      gl.uniform1i(this.loc.uUseTex, 0);
      gl.uniform3fv(this.loc.uColor, this.metal);
      gl.uniform1f(this.loc.uSpec, 0.35); gl.uniform1f(this.loc.uShine, 28);
      gl.drawArrays(gl.TRIANGLES, 0, this.nRest);
    }
  }

  /* ---------- Size comparison (SVG, millimetre units) ---------- */
  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  function artSVG(art, x, y, w, h, id) {
    return '<defs><clipPath id="' + id + '"><rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '"/></clipPath>' +
      '<filter id="' + id + 's" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="' + (h * 0.012) + '" stdDeviation="' + (h * 0.02) + '" flood-color="#000" flood-opacity=".28"/></filter></defs>' +
      '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" fill="#ccc" filter="url(#' + id + 's)"/>' +
      '<image href="' + esc(art) + '" x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" preserveAspectRatio="xMidYMid slice" clip-path="url(#' + id + ')"/>' +
      '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" fill="none" stroke="rgba(255,255,255,.35)" stroke-width="2"/>';
  }

  function sizeSVG(art, sizes, selectedLabel) {
    // Draw every size side by side at true scale with an A4 sheet for reference.
    const gap = 140, pad = 120, a4 = { w: 210, h: 297 };
    const maxH = Math.max(a4.h, ...sizes.map(s => s.h));
    let x = pad, items = '';
    sizes.forEach((s, i) => {
      const sel = s.label === selectedLabel;
      const y0 = pad + 80 + (maxH - s.h);
      items += artSVG(art, x, y0, s.w, s.h, 'mwmv-size' + i) +
        (sel ? '<rect x="' + (x - 14) + '" y="' + (y0 - 14) + '" width="' + (s.w + 28) + '" height="' + (s.h + 28) + '" fill="none" stroke="#173c32" stroke-width="8" rx="6"/>' : '') +
        '<text x="' + (x + s.w / 2) + '" y="' + (pad + 80 + maxH + 70) + '" text-anchor="middle" font-size="44" font-weight="' + (sel ? 700 : 500) + '" fill="#202522">' + esc(H.fmtCm(s.w) + ' × ' + H.fmtCm(s.h)) + '</text>' +
        (sel ? '<text x="' + (x + s.w / 2) + '" y="' + (pad + 80 + maxH + 125) + '" text-anchor="middle" font-size="36" fill="#173c32">Selected</text>' : '');
      x += s.w + gap;
    });
    const ay = pad + 80 + (maxH - a4.h);
    items += '<rect x="' + x + '" y="' + ay + '" width="' + a4.w + '" height="' + a4.h + '" fill="#fffdf8" stroke="#8c8471" stroke-width="3" stroke-dasharray="12 8"/>' +
      '<text x="' + (x + a4.w / 2) + '" y="' + (ay + a4.h / 2 + 14) + '" text-anchor="middle" font-size="40" fill="#4a524c">A4</text>' +
      '<text x="' + (x + a4.w / 2) + '" y="' + (pad + 80 + maxH + 70) + '" text-anchor="middle" font-size="36" fill="#4a524c">A4 sheet</text>';
    const VW = x + a4.w + pad, VH = pad + 80 + maxH + 170;
    return '<svg viewBox="0 0 ' + VW + ' ' + VH + '" role="img" aria-label="Size comparison drawn to scale with an A4 sheet" xmlns="http://www.w3.org/2000/svg" font-family="Montserrat, Arial, sans-serif">' +
      '<rect width="' + VW + '" height="' + VH + '" fill="#f6f1e7"/>' + items + '</svg>';
  }

  /* ---------- The custom element ---------- */
  class MahiMetalViewer extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      try { this.cfg = JSON.parse(this.getAttribute('data-config') || '{}'); } catch (e) { this.cfg = {}; }
      this.cfg.variants = (this.cfg.variants || []).map(v => Object.assign({}, v, { dims: H.parseSize(v.size), mat: H.parseMaterial(v.material) }));
      this.tab = 'photos';
      this.scene = 'lounge';
      this.querySelectorAll('[data-mv-tab]').forEach(b => b.addEventListener('click', () => this.show(b.dataset.mvTab)));
      this.querySelector('[role=tablist]').addEventListener('keydown', e => {
        const tabs = [...this.querySelectorAll('[data-mv-tab]:not([hidden])')], i = tabs.indexOf(document.activeElement);
        if (i < 0) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); const n = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length]; n.focus(); this.show(n.dataset.mvTab); }
      });
      this.querySelectorAll('[data-mv-view]').forEach(b => b.addEventListener('click', () => { if (this.gl) { this.gl.view(b.dataset.mvView); this.kick(); this.say3d(b.dataset.mvView); } }));
      // Room scenes: calibrated photos of real rooms when supplied; otherwise the product's own styled mockups.
      this.scenes = (this.cfg.scenes || []).map(H.parseScene).filter(Boolean);
      // Product room scenes (lounge, bedroom, office) come first; other styled mockups are used only when there are none.
      const all = this.cfg.mockups || [], order = ['Lounge', 'Bedroom', 'Office'];
      const rooms = all.filter(m => m.room).sort((a, b) => order.indexOf(a.room) - order.indexOf(b.room));
      this.mockups = rooms.length ? rooms : all;
      this.scene = 0;
      const list = this.scenes.length ? this.scenes.map(x => x.name) : this.mockups.map((m, i) => m.room || (this.mockups.length > 1 ? 'Photo ' + (i + 1) : ''));
      const bar = this.querySelector('[data-mv-scenes]');
      bar.innerHTML = list.filter(Boolean).map((name, i) => '<button type="button" class="mw-mv__btn" data-mv-scene="' + i + '" aria-pressed="' + (i === 0) + '">' + esc(name) + '</button>').join('');
      bar.hidden = !bar.children.length;
      bar.querySelectorAll('[data-mv-scene]').forEach(b => b.addEventListener('click', () => { this.scene = Number(b.dataset.mvScene); this.renderScene(); }));
      this.querySelector('[data-mv-tab="rooms"]').hidden = !this.scenes.length && !this.mockups.length;
      this.querySelector('[data-mv-back-to-photos]').addEventListener('click', () => this.show('photos'));
      this.variant = this.currentVariant();
      this.update();
    }
    currentVariant() {
      const form = document.getElementById(this.cfg.formId) || document.querySelector('form[action*="/cart/add"]');
      const id = form && form.querySelector('[name="id"]') ? form.querySelector('[name="id"]').value : null;
      return this.cfg.variants.find(v => String(v.id) === String(id)) || this.cfg.variants.find(v => String(v.id) === String(this.cfg.initialVariant)) || this.cfg.variants[0];
    }
    // Orientation: the shopper's Portrait/Landscape choice on the form wins; personalised designs otherwise follow
    // the example artwork; fixed designs keep the size as listed (e.g. 400 × 300 mm is landscape).
    orientation(art) {
      const form = document.getElementById(this.cfg.formId) || document.querySelector('form[action*="/cart/add"]');
      const sel = form && form.querySelector('[name="properties[Orientation]"]');
      const val = sel ? String(sel.value || '').toLowerCase() : '';
      if (/landscape/.test(val)) return 'landscape';
      if (/portrait/.test(val)) return 'portrait';
      if (this.cfg.personalised && art) return art.w > art.h ? 'landscape' : 'portrait';
      return null;
    }
    orient(d, o) {
      if (!d || !o) return d;
      const long = Math.max(d.w, d.h), short = Math.min(d.w, d.h);
      return o === 'landscape' ? { w: long, h: short } : { w: short, h: long };
    }
    checkVariant() {
      const v = this.currentVariant();
      const key = v ? v.id + '|' + this.orientation(H.pickArtwork(this.cfg.artworks, v.tier)) : '';
      if (v && key !== this._key) { this.variant = v; this.update(); }
    }
    show(tab) {
      this.tab = tab;
      this.querySelectorAll('[data-mv-tab]').forEach(b => { const on = b.dataset.mvTab === tab; b.setAttribute('aria-selected', String(on)); b.tabIndex = on ? 0 : -1; });
      this.querySelectorAll('[data-mv-panel]').forEach(p => { p.hidden = p.dataset.mvPanel !== tab; });
      const gallery = this.closest('product-gallery');
      if (gallery) gallery.classList.toggle('mw-mv-on', tab !== 'photos');
      this.querySelector('[data-mv-example]').hidden = !this.cfg.personalised || tab === 'photos';
      if (tab === '3d') this.start3d();
      if (tab === 'rooms') this.renderScene();
      if (tab === 'size') this.renderSize();
    }
    // Selected variant's real details and price, shown on every view
    update() {
      const v = this.variant; if (!v) return;
      const m = v.mat;
      // Artwork for this variant (personalised design levels have their own example designs)
      const art = H.pickArtwork(this.cfg.artworks, v.tier);
      const artChanged = art && (!this.art || art.src !== this.art.src);
      if (art) this.art = art;
      this._orient = this.orientation(this.art);
      this._key = v.id + '|' + this._orient;
      const d = this.orient(v.dims, this._orient);
      this.dims = d;
      if (artChanged && this.gl && this._texReady) this.loadTexture();
      const spec = d ? H.fmtCm(d.w) + ' × ' + H.fmtCm(d.h) + (m.thickness ? ' · ' + m.thickness + ' mm ' : ' · ') + m.name : v.size;
      this.querySelectorAll('[data-mv-spec]').forEach(el => { el.textContent = spec; });
      this.querySelectorAll('[data-mv-price]').forEach(el => { el.textContent = v.price; });
      // Installation view: only once hardware for this material is confirmed
      const hw = (this.cfg.hardware || {})[m.key] || '';
      const installTab = this.querySelector('[data-mv-tab="install"]');
      installTab.hidden = !hw;
      this.querySelector('[data-mv-install-text]').textContent = hw;
      if (!hw && this.tab === 'install') this.show('photos');
      if (this.gl && this._texReady && d) { this.gl.setPanel(d.w, d.h, m.thickness, this.metalColour(m.key)); this.kick(); this.label3d(); }
      if (this.tab === 'rooms') this.renderScene();
      if (this.tab === 'size') this.renderSize();
    }
    metalColour(key) { return key.indexOf('alloy') === 0 ? [0.78, 0.79, 0.8] : [0.6, 0.63, 0.66]; }
    /* --- 3D, loaded on demand --- */
    start3d() {
      if (this._3dStarted) { this.kick(); return; }
      this._3dStarted = true;
      const canvas = this.querySelector('[data-mv-canvas]'), status = this.querySelector('[data-mv-3d-status]');
      const fail = msg => { this.querySelector('[data-mv-3d-fallback]').hidden = false; this.querySelector('[data-mv-3d-fallback-text]').textContent = msg; canvas.hidden = true; status.textContent = ''; };
      try { this.gl = new PanelGL(canvas); } catch (e) { fail('3D isn’t available on this device or browser. The photos show the print instead.'); return; }
      this._fail = fail;
      status.textContent = 'Loading the 3D view…';
      this.loadTexture(() => {
        this.gl.s.yaw = -0.9; this.gl.view('angle');
        this.bindOrbit(canvas);
        this.dispatchEvent(new CustomEvent('mw-mv:3d-ready', { bubbles: true }));
      });
      if ('ResizeObserver' in window) new ResizeObserver(() => this.kick()).observe(canvas);
    }
    loadTexture(first) {
      const status = this.querySelector('[data-mv-3d-status]');
      const job = this._texJob = (this._texJob || 0) + 1;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (job !== this._texJob) return;
        try { this.gl.setTexture(img); } catch (e) { this._fail('The artwork couldn’t be loaded into the 3D view. The photos show the print instead.'); return; }
        this._texReady = true;
        if (status.textContent.indexOf('Loading') === 0) status.textContent = '';
        const d = this.dims, m = this.variant.mat;
        this.gl.setPanel(d.w, d.h, m.thickness, this.metalColour(m.key));
        if (first) first();
        this.label3d();
        this.kick();
      };
      img.onerror = () => { if (job === this._texJob) this._fail('The artwork couldn’t be loaded into the 3D view. The photos show the print instead.'); };
      img.src = this.art.src3d || this.art.src;
    }
    label3d() {
      const g = this.gl; if (!g) return;
      const note = this.querySelector('[data-mv-crop-note]');
      note.hidden = !g.cropped;
    }
    say3d(view) {
      const t = { front: 'Front view', edge: 'Edge close-up: the panel is ' + (this.variant.mat.thickness || '') + ' mm thick. Drag to zoom back out.', back: 'Back view: plain metal panel. Mounting hardware isn’t shown.' }[view] || '';
      this.querySelector('[data-mv-3d-status]').textContent = t;
    }
    kick() {
      if (!this.gl || this._raf) return;
      const loop = () => {
        this._raf = null;
        const moving = this.gl.step();
        this.gl.draw();
        if (moving || this._dragging) this._raf = requestAnimationFrame(loop);
      };
      this._raf = requestAnimationFrame(loop);
    }
    bindOrbit(canvas) {
      const g = this.gl, pts = new Map();
      let last = null, pinch = null;
      canvas.addEventListener('pointerdown', e => { if (g.zoom < 0.25) { g.zoom = 1; g.t.tx = 0; g.t.ty = 0; } canvas.setPointerCapture(e.pointerId); pts.set(e.pointerId, { x: e.clientX, y: e.clientY }); last = { x: e.clientX, y: e.clientY }; this._dragging = true; if (pts.size === 2) { const [a, b] = [...pts.values()]; pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), z: g.zoom }; } this.kick(); });
      canvas.addEventListener('pointermove', e => {
        if (!pts.has(e.pointerId)) return;
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pts.size === 2 && pinch) { const [a, b] = [...pts.values()]; const d = Math.hypot(a.x - b.x, a.y - b.y); g.zoom = Math.min(1.6, Math.max(0.03, pinch.z * pinch.d / d)); }
        else if (last) {
          g.t.yaw += (e.clientX - last.x) * 0.01; g.t.pitch = Math.max(-1.1, Math.min(1.1, g.t.pitch + (e.clientY - last.y) * 0.008));
          g.t.tx = 0; g.t.ty = 0; last = { x: e.clientX, y: e.clientY };
        }
        this.kick();
      });
      const end = e => { pts.delete(e.pointerId); if (pts.size < 2) pinch = null; if (!pts.size) { this._dragging = false; last = null; } };
      canvas.addEventListener('pointerup', end); canvas.addEventListener('pointercancel', end);
      canvas.addEventListener('wheel', e => { e.preventDefault(); g.zoom = Math.min(1.6, Math.max(0.03, g.zoom * (e.deltaY > 0 ? 1.08 : 0.92))); this.kick(); }, { passive: false });
      canvas.addEventListener('keydown', e => {
        const k = { ArrowLeft: [-0.15, 0], ArrowRight: [0.15, 0], ArrowUp: [0, -0.1], ArrowDown: [0, 0.1] }[e.key];
        if (k) { e.preventDefault(); if (g.zoom < 0.25) { g.zoom = 1; g.t.tx = 0; g.t.ty = 0; } g.t.yaw += k[0]; g.t.pitch = Math.max(-1.1, Math.min(1.1, g.t.pitch + k[1])); g.t.tx = 0; this.kick(); }
      });
    }
    /* --- Room scenes and size --- */
    renderScene() {
      const v = this.variant; if (!v || !this.dims) return;
      const dims = this.dims;
      this.querySelectorAll('[data-mv-scene]').forEach(b => b.setAttribute('aria-pressed', String(Number(b.dataset.mvScene) === this.scene)));
      const box = this.querySelector('[data-mv-scene-art]'), note = this.querySelector('[data-mv-scene-note]');
      const sc = this.scenes[this.scene];
      if (sc) {
        // The print is placed on the photographed wall at its real size for the selected variant.
        const p = H.placeOnPhoto(sc, dims.w, dims.h);
        box.innerHTML = '<div class="mw-mv__photo" style="aspect-ratio:' + sc.imgW + ' / ' + sc.imgH + '">' +
          '<img src="' + esc(sc.src) + '" alt="' + esc(sc.name + ' with the ' + H.fmtCm(dims.w) + ' by ' + H.fmtCm(dims.h) + ' print on the wall') + '" loading="lazy">' +
          '<div class="mw-mv__print" style="left:' + p.left + '%;top:' + p.top + '%;width:' + p.width + '%;height:' + p.height + '%"><img src="' + esc(this.art.src) + '" alt=""></div></div>';
        note.textContent = 'Illustrative photo. The print is shown at the selected size, scaled from a measured object in the room.';
      } else {
        const m = this.mockups[this.scene] || this.mockups[0];
        box.innerHTML = m ? '<div class="mw-mv__photo"><img src="' + esc(m.src) + '" alt="' + esc(m.alt) + '" loading="lazy"></div>' : '';
        note.textContent = (m && m.room ? 'Illustrative ' + m.room.toLowerCase() + ' scene, not to scale.' : 'Illustrative styled photo, not to scale.') + ' Use the Size tab to compare sizes.';
      }
    }
    renderSize() {
      const v = this.variant; if (!v) return;
      const seen = {}, sizes = [];
      this.cfg.variants.forEach(x => { if (x.dims && !seen[x.size]) { seen[x.size] = 1; sizes.push(Object.assign({ label: x.size }, this.orient(x.dims, this._orient))); } });
      this.querySelector('[data-mv-size-art]').innerHTML = sizeSVG(this.art.src, sizes, v.size);
      // Prices for each size in the selected material, from Shopify variants
      const rows = sizes.map(s => {
        const match = this.cfg.variants.find(x => x.size === s.label && x.material === v.material);
        const sel = s.label === v.size;
        return '<tr' + (sel ? ' class="is-selected"' : '') + '><th scope="row">' + esc(H.fmtCm(s.w) + ' × ' + H.fmtCm(s.h)) + (sel ? ' <span>(selected)</span>' : '') + '</th><td>' + esc(match ? match.price : '—') + (match && !match.available ? ' · unavailable' : '') + '</td></tr>';
      }).join('');
      this.querySelector('[data-mv-size-rows]').innerHTML = rows;
      this.querySelector('[data-mv-size-material]').textContent = v.material;
      const imgRatio = this.art.w / this.art.h;
      const cropped = sizes.filter(s => Math.abs(s.w / s.h - imgRatio) > 0.01).map(s => H.fmtCm(s.w) + ' × ' + H.fmtCm(s.h));
      const n = this.querySelector('[data-mv-size-crop]');
      n.hidden = !cropped.length;
      n.textContent = cropped.length ? 'The artwork is shown fitted to each size’s proportions. ' + cropped.join(', ') + ' is a different shape from the original artwork, so the edges are trimmed here.' : '';
    }
  }

  /* ---------- Mount into the theme's product gallery (survives the theme re-rendering on variant change) ---------- */
  let viewer = null;
  function mount() {
    const tpl = document.querySelector('template[data-mw-mv-template]');
    if (!tpl) return;
    if (!viewer) {
      if (!customElements.get('mahi-metal-viewer')) customElements.define('mahi-metal-viewer', MahiMetalViewer);
      viewer = tpl.content.firstElementChild.cloneNode(true);
    }
    const list = document.querySelector('product-gallery .product-gallery__image-list');
    if (!list) return;
    if (viewer.parentNode !== list) list.insertBefore(viewer, list.firstChild);
    const gallery = viewer.closest('product-gallery');
    if (gallery && viewer.tab) gallery.classList.toggle('mw-mv-on', viewer.tab !== 'photos');
  }
  function watch() {
    mount();
    new MutationObserver(() => { if (!viewer || !document.contains(viewer) || !viewer.closest('product-gallery')) mount(); })
      .observe(document.body, { childList: true, subtree: true });
    const check = () => { if (viewer && viewer.checkVariant) viewer.checkVariant(); };
    document.addEventListener('variant:change', () => setTimeout(check, 0));
    document.addEventListener('change', () => setTimeout(check, 50));
    setInterval(() => { if (!document.hidden) check(); }, 600);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch); else watch();
})(typeof window !== 'undefined' ? window : this);
